/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React from "react";
import {IconVehicle} from "../../../../../../../components/buttons/IconButtons";
import {CustomTextField} from "../../../../../../../components/CustomTextField";
import {TRANSPORT_FIELD_LENGTH} from "../../../../../MaterialControl/Model/Model";
import {isNotEmpty} from "../../../../../../../Apis/functions";

/**
 * Transport of a folio as the guard reads it at the gate.
 *
 * The transport is optional for the requester, so only what already travels
 * with the folio is read; what was left blank is the one the guard completes
 * while the vehicle is at the gate.
 *
 * @param {Object} props - Values of the transport and its handlers.
 * @param {Function} props.t - Translation handler.
 * @param {Object} props.item - Folio the transport belongs to.
 * @param {Object} props.gateLog - Gate log captured by the guard.
 * @param {Function} props.handleChange - Change handler of the fields of the gate log.
 * @param {boolean} props.emptyFields - Whether the required fields are marked.
 * @param {boolean} props.disabled - Whether the transport is only read.
 *
 * @returns {JSX.Element} Block with the carrier and the vehicle of the folio.
 */
const GuardTransportSection = ({
                                   t,
                                   item,
                                   gateLog,
                                   handleChange,
                                   emptyFields = false,
                                   disabled = false
                               }) => {

    const isCarrierRegistered = isNotEmpty(item?.carrierCompany);
    const isVehicleRegistered = isNotEmpty(item?.vehicleInformation);

    return (
        <div className="border-full-shades-300 background-general-00 border-radius-8px my-3">
            <div className={"d-flex align-items-center p-2"}>
                <div className={"mr-2 d-flex align-items-center"}><IconVehicle/></div>
                <p className={"fs-14px ibl-font-w-600"}>{t("transport")}</p>
            </div>
            <div className={"border-bottom-shades-300"}/>

            <div className={"row input-padding-top pb-3"}>
                <div className={"col-12"}>
                    <CustomTextField
                        id='txt_gts_carrier_company'
                        length={TRANSPORT_FIELD_LENGTH.carrierCompany}
                        name='carrierCompany'
                        value={isCarrierRegistered ? item?.carrierCompany : gateLog?.carrierCompany}
                        onChange={handleChange}
                        trans='carrier_company'
                        required={disabled || !isCarrierRegistered}
                        emptyFields={emptyFields && !disabled && !isCarrierRegistered}
                        disabled={disabled || isCarrierRegistered}
                        placeholder={t('enter_carrier_company_name')}
                    />
                </div>
                <div className={"col-12"}>
                    <CustomTextField
                        id='txt_gts_vehicle_information'
                        length={TRANSPORT_FIELD_LENGTH.vehicleInformation}
                        name='vehicleInformation'
                        value={isVehicleRegistered ? item?.vehicleInformation : gateLog?.vehicleInformation}
                        onChange={handleChange}
                        trans='vehicle_information'
                        multiline={true}
                        required={disabled || !isVehicleRegistered}
                        emptyFields={emptyFields && !disabled && !isVehicleRegistered}
                        disabled={disabled || isVehicleRegistered}
                        placeholder={t('add_optional_vehicle_details_such_as_plate_number_color_model_or_any_relevant_notes')}
                    />
                </div>
            </div>
        </div>
    );
};

export default GuardTransportSection;
